import { CreateEntities } from "@shared_packages/toolkit/types";
import type { DetailFieldGroup } from "./detail-cards-config.types";

function getNestedValue(data: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[key];
  }, data);
}

/**
 * Reusable field group for a related company entity.
 * Shows: Company ID (link) + Company name + Company trading name.
 * Use for `owner`, `company`, `managedBy`, etc.
 */
export function buildRelatedCompanyFieldGroup<T>(
  accessorKey: string,
  opts: { companyDetailsRoute: string }
): DetailFieldGroup<T> {
  return [
    {
      label: "Company ID",
      displayType: "link",
      accessorKey: `${accessorKey}._id`,
      getHref: (data) => {
        const entity = (data as Record<string, unknown>)[accessorKey];
        if (entity == null || typeof entity !== "object") return undefined;
        const id = String((entity as Record<string, unknown>)._id ?? "").trim();
        return id ? `${opts.companyDetailsRoute}${id}` : undefined;
      },
      getLabel: (data) => {
        const entity = (data as Record<string, unknown>)[accessorKey];
        if (entity == null || typeof entity !== "object") return undefined;
        const id = String((entity as Record<string, unknown>)._id ?? "").trim();
        return id || undefined;
      },
    },
    {
      label: "Company Name",
      displayType: "text",
      accessorKey: `${accessorKey}.name`,
    },
    {
      label: "Company Trading Name",
      displayType: "text",
      accessorKey: `${accessorKey}.tradingName`,
    },
  ];
}

/**
 * Reusable field group for created-by data.
 * Shows: Created by entity + Created by ID (link) + Name + Email (mailto:).
 * Convention: entity accessor = `${accessorKey}Entity`.
 */
export function buildCreatedByFieldGroup<T>(
  accessorKey: string,
  opts: { userDetailsRoute: string }
): DetailFieldGroup<T> {
  const entityAccessorKey = `${accessorKey}Entity`;
  return [
    {
      label: "Created By Entity",
      displayType: "text",
      accessorKey: entityAccessorKey,
      getValue: (data) =>
        createdByEntityLabel(
          getNestedValue(data, entityAccessorKey) as
            | CreateEntities
            | string
            | null
        ),
    },
    {
      label: "Created By ID",
      displayType: "text",
      hidden: (data) =>
        getNestedValue(data, entityAccessorKey) === CreateEntities.USER,
      getValue: (data) => {
        const nestedId = getNestedValue(data, `${accessorKey}._id`);
        const direct = getNestedValue(data, accessorKey);
        const idValue =
          nestedId ??
          (typeof direct === "string" || typeof direct === "number"
            ? direct
            : undefined);
        const id = idValue == null ? "" : String(idValue).trim();
        return id || undefined;
      },
    },
    {
      label: "Created By Name",
      displayType: "link",
      getValue: (data) => {
        const person = getNestedValue(data, accessorKey);
        if (person == null || typeof person !== "object") return undefined;
        const p = person as Record<string, unknown>;
        const name = [
          String(p.firstName ?? "").trim(),
          String(p.lastName ?? "").trim(),
        ]
          .filter(Boolean)
          .join(" ");
        return name || undefined;
      },
      getHref: (data) => {
        if (getNestedValue(data, entityAccessorKey) !== CreateEntities.USER) {
          return undefined;
        }
        const nestedId = getNestedValue(data, `${accessorKey}._id`);
        const direct = getNestedValue(data, accessorKey);
        const idValue =
          nestedId ??
          (typeof direct === "string" || typeof direct === "number"
            ? direct
            : undefined);
        const id = idValue == null ? "" : String(idValue).trim();
        return id ? `${opts.userDetailsRoute}${id}` : undefined;
      },
    },
    {
      label: "Created By Email",
      displayType: "email",
      accessorKey: `${accessorKey}.email`,
    },
  ];
}

export function createdByEntityLabel(
  entity: CreateEntities | string | null | undefined
): string | undefined {
  if (entity == null) return undefined;
  switch (entity) {
    case CreateEntities.USER:
      return "User";
    case CreateEntities.EXTERNAL_INTEGRATION:
      return "External integration";
    case CreateEntities.SYSTEM:
      return "System";
    default:
      return String(entity);
  }
}
